import layer from './layer';

import _cloneDeep from 'lodash/cloneDeep';
import _findIndex from 'lodash/findIndex';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';

export default {
  mixins: [layer],
  props: {
    // Property on supplied items that contains its children
    propertiesChildren: {
      type: String,
      required: false,
      default: 'sublayers'
    },
    // Property on supplied items used to keep track of node state.
    // The value of this property has to be unique among all items.
    propertiesKey: {
      type: String,
      required: false,
      default: 'id'
    },
    // When true will make v-model return the complete object instead of just the key
    returnObject: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    /**
     * @Override
     */
    computedValue () {
      let value;

      if (this.isVisibleValueBoolean) {
        value = this.visibleValue;
      } else {
        if (this.returnObject) {
          value = this.properties;
        } else {
          value = this.value ? this.value : this.propertiesValue;
        }
      }

      return value;
    },
    propertiesValue () {
      return this.properties[this.propertiesKey];
    },
    hasChildren () {
      return !_isEmpty(this.children);
    },
    children () {
      return _get(this.properties, this.propertiesChildren, []);
    },
    hasVisibleValues () {
      return !_isEmpty(this.visibleValue);
    },
    allDescendantLeafs () {
      const leafs = [];

      const searchTree = item => {
        if (!_isEmpty(item[this.propertiesChildren])) {
          item[this.propertiesChildren].forEach(child => searchTree(child));
        } else {
          leafs.push(item);
        }
      };

      searchTree(this.properties);
      return leafs;
    },
    allDescendantLeafsVisible () {
      let visible;

      if (this.isVisibleValueMultiple) {
        visible = this.hasChildren &&
          this.hasVisibleValues &&
          this.allDescendantLeafs.every(leaf => {
            return this.visibleValue.some(visible => {
              const leafValue = this.returnObject ? leaf : leaf[this.propertiesKey];
              return _isEqual(visible, leafValue);
            });
          });
      } else if (this.isVisibleValueBoolean) {
        visible = this.visibleValue;
      } else {
        visible = _isEqual(this.computedValue, this.visibleValue);
      }

      return visible;
    },
    someDescendantLeafsVisible () {
      let visible;

      if (this.isVisibleValueMultiple) {
        visible = this.hasVisibleValues &&
          this.allDescendantLeafs.some(leaf => {
            return this.visibleValue.some(visible => {
              const leafValue = this.returnObject ? leaf : leaf[this.propertiesKey];
              return _isEqual(visible, leafValue);
            });
          });
      } else if (this.isVisibleValueBoolean) {
        visible = this.visibleValue;
      } else {
        visible = _isEqual(this.computedValue, this.visibleValue);
      }

      return visible;
    },
    indeterminate () {
      return this.hasChildren &&
        this.hasVisibleValues &&
        this.someDescendantLeafsVisible &&
        !this.allDescendantLeafsVisible;
    },
    /**
     * @Override
     */
    visible () {
      let visible;

      if (this.hasChildren) {
        visible = this.indeterminate || this.allDescendantLeafsVisible;
      } else {
        if (this.isVisibleValueMultiple) {
          visible = this.visibleValue.some(visible => _isEqual(visible, this.computedValue));
        } else if (this.isVisibleValueBoolean) {
          visible = this.visibleValue;
        } else {
          visible = _isEqual(this.computedValue, this.visibleValue);
        }
      }

      return visible;
    }
  },
  methods: {
    /**
     * @Override
     */
    setVisible (visible) {
      if (this.visible !== visible) {
        if (this.isVisibleValueMultiple) {
          if (this.hasChildren) {
            this.setAllDescendantLeafsVisible(visible);
          } else {
            const clone = _cloneDeep(this.visibleValue);
            let ix = _findIndex(clone, item => _isEqual(item, this.computedValue));

            if (visible) {
              if (ix === -1) {
                clone.push(_cloneDeep(this.computedValue));
              }
            } else {
              clone.splice(ix, 1);
            }

            this.$emit('input', clone);
          }
        } else if (this.isVisibleValueBoolean) {
          this.$emit('input', visible);
        } else {
          this.$emit('input', visible ? _cloneDeep(this.computedValue) : null);
        }
      }
    },
    setAllDescendantLeafsVisible (visible) {
      const clone = _cloneDeep(this.visibleValue);

      this.allDescendantLeafs.forEach(leaf => {
        const leafValue = this.returnObject ? leaf : leaf[this.propertiesKey];
        let ix = _findIndex(clone, item => _isEqual(item, leafValue));

        if (visible) {
          if (ix === -1) {
            clone.push(_cloneDeep(leafValue));
          }
        } else {
          clone.splice(ix, 1);
        }
      });

      this.$emit('input', clone);
    }
  }
};
